// Figma frame 16 · Vocabulary health & merges (34:2127) — US-122.
//
// PACKAGING, NOT CONSTRUCTION. Everything shown here already exists and
// already runs: `backend/corpus/health.py` computes the three finding kinds
// and ranks each to TOP_N, and Pass-2 reconciliation auto-runs on publish.
// This screen puts a buyer-facing surface on top of them and hands the
// actual accept / reject work to `/corpora/:corpusId/merges`, which shipped
// long ago — it does not reimplement that queue.
//
// A3 — ASSIGNMENT IS THE NEW PART (PRD 16 §4). The three finding kinds were
// already "the right three questions"; what turned them from *interesting*
// into *assign this to someone* is the owner / status / snooze on each row.
// So the findings render as a Table rather than a List: a work-list is a grid
// of several columns per row, and `ListRow` makes the entire row one link,
// which cannot contain the controls. The volume bound and the stated real
// total carry over unchanged — `Table` enforces both itself.
//
// Dismissal (done, or snoozed into the future) is applied by the SERVER,
// before its TOP_N cap, so finishing the top item promotes the eleventh
// rather than leaving a hole. `include_dismissed` is the only way back, hence
// the toggle — a work-list you cannot un-snooze from is a one-way door.
//
// TAB DIVERGENCE FROM THE FRAME, DELIBERATE. Figma draws three tabs:
// MERGE QUEUE / SINGLETONS / REJECTED. `REJECTED` is a property of the merge
// reviewer, which already renders it and which this screen links to; and
// `stalls` — a required finding kind — has no drawn home at all. So the third
// tab is STALLS. Recorded for `/crg-ui`, which will otherwise measure this as
// a text discrepancy and "fix" it back.

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import "./content.css";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  SkeletonStack,
  StatRow,
  StatTile,
  Table,
  Toggle,
  type TableColumn,
} from "../../ui";
import {
  snoozeUntil,
  useAssignFinding,
  useVocabWorkList,
  type AssignInput,
  type AssignmentStatus,
  type WorkListFinding,
} from "./api";
import { useCorpusMerges } from "../../review/corpus/merges-api";
// A3 (this screen) is where a writer PICKS UP work; A1 is where the product
// proves the work paid off. They were two screens with no path between them,
// so a writer who fixed a stall had to go hunting for the evidence. These are
// read-only imports of the analytics screen's own hook and copy — importing
// beats restating, for the same reason the look-up rate has one renderer.
import { useFixAndProve } from "../analytics/api";
import type { LedgerEntry } from "../analytics/api";
import { formatMagnitude } from "../analytics/format";
import {
  LOOKUPS_FELL,
  LOOKUPS_FLAT,
  LOOKUPS_ROSE,
  PER_VISIT_LABEL,
} from "../analytics/metricLabels";
import { hrefs } from "../../shell/hrefs";

const DAYS = 30;
/** The list is a work-list, not an inventory — the server ranks, we cap. */
const MAX_ROWS = 10;
const SNOOZE_DAYS = 7;

/** The verdict words, per `LedgerEntry["verdict"]`. Imported from the
 *  analytics screen rather than restated: two surfaces wording the same
 *  verdict differently is the drift that makes a number look untrustworthy. */
const VERDICT_WORDS: Record<string, string> = {
  improved: LOOKUPS_FELL,
  worsened: LOOKUPS_ROSE,
  unchanged: LOOKUPS_FLAT,
};

const STATUS_OPTIONS: { value: AssignmentStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "done", label: "Done" },
];

/** The slug half of a finding key, which is `<kind>:<doc_id>#<slug>`.
 *
 *  Split on the LAST `#`: the kind prefix is one of three known literals and
 *  carries no `#`, but a doc id is not guaranteed to be free of one. */
export function slugFromKey(key: string): string | null {
  const hash = key.lastIndexOf("#");
  return hash === -1 ? null : key.slice(hash + 1);
}

/** The Fix-and-Prove entry for a finding, or `undefined` when there is none.
 *
 *  THE JOIN IS `(doc_id, slug)` AND IT NEEDS NO NEW FIELD. A ledger entry
 *  carries `doc_id` + `slug`; the finding key ends in `slug_of(term_id)` and
 *  its `doc_ids` holds the one document (a `stalls` finding is always
 *  single-doc). Joining on the slug rather than a `term_id` is not a
 *  convenience — editing a definition re-mints `term_id`
 *  (`<slug>-<sha256(definition)[:6]>`), so the id on either side of the very
 *  rewrite this links to is different, and a `term_id` join would miss
 *  precisely the rows that have proof.
 *
 *  `undefined` when nothing matches, and the caller renders NO link at all.
 *  A link into an empty state is worse than no link: an absent entry is not
 *  an entry showing nothing. */
export function fixAndProveFor(
  finding: WorkListFinding,
  entries: LedgerEntry[],
): LedgerEntry | undefined {
  const slug = slugFromKey(finding.key);
  if (slug === null) return undefined;
  return entries.find(
    (entry) => entry.slug === slug && finding.doc_ids.includes(entry.doc_id),
  );
}

/** The proof sentence for a matched entry.
 *
 *  A rewrite that exists but cannot yet be judged still gets a line — the
 *  ledger row is real and says why — but it must never borrow the shape of a
 *  verdict. `formatMagnitude` is unsigned because the direction is already in
 *  the words; pairing them would read "looked up less often −62%". */
export function proofLabel(entry: LedgerEntry): string {
  const on = entry.edited_at.slice(0, 10);
  if (entry.verdict === "not_measured") {
    return `Rewritten ${on} — not enough traffic either side to judge it yet`;
  }
  const words = VERDICT_WORDS[entry.verdict] ?? entry.verdict;
  return `Rewritten ${on} — ${words} ${formatMagnitude(entry.lookups_delta_pct)} ${PER_VISIT_LABEL}`;
}

/** Rows for one tab, mirroring `backend/corpus/health.py::_rank`.
 *
 *  RESOLVED ROWS ARE APPENDED PAST THE CAP, NOT RANKED INTO IT. They score
 *  0.0, so under a plain sort-then-slice any ten live findings push them off
 *  the bottom — the same silent disappearance the backend added them to
 *  prevent, one layer up and harder to see. The backend already appends past
 *  its own TOP_N; this cap is a second one and would have undone that. */
export function packRows(all: WorkListFinding[]): WorkListFinding[] {
  const active = all
    .filter((finding) => !finding.unranked_reason)
    .sort((a, b) => b.score - a.score);
  const resolved = all
    .filter((finding) => finding.unranked_reason)
    .sort((a, b) => a.term.localeCompare(b.term));
  return [...active.slice(0, MAX_ROWS), ...resolved];
}

type TabKey = WorkListFinding["kind"];

const TABS: { key: TabKey; label: string; empty: string }[] = [
  {
    key: "inconsistent",
    label: "Merge queue",
    empty: "Every concept is defined one way across your docs.",
  },
  {
    key: "unexplained",
    label: "Singletons",
    empty: "No jargon is used once and left unexplained.",
  },
  {
    key: "stalls",
    label: "Stalls",
    empty: "No term is holding readers up.",
  },
];

// `useVocabularyHealth` requires a corpus id and resolves it as the org slug,
// the same assumption `reports/monthly.py` makes and `sources/schema.sql`
// backfills. Kept identical here on purpose: two surfaces guessing the corpus
// differently would disagree about the same org's health.
export function VocabularyHealthScreen({ orgSlug }: { orgSlug: string }) {
  const [tab, setTab] = useState<TabKey>("inconsistent");
  const [showDismissed, setShowDismissed] = useState(false);
  const health = useVocabWorkList(orgSlug, DAYS, showDismissed);
  const merges = useCorpusMerges(orgSlug);
  // A1's ledger, for the proof line on a resolved row. Read-only and
  // best-effort: the work-list must render fully whether or not this resolves.
  const ledger = useFixAndProve(orgSlug, DAYS);

  const pending = merges.data?.proposals.filter(
    (proposal) => proposal.review_state === "pending",
  );

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Vocabulary health"
        description="Where the same concept is defined two different ways, where jargon is used once and never explained, and where readers stall."
        actions={
          <Link to="/corpora/$corpusId/merges" params={{ corpusId: orgSlug }}>
            <Button variant="primary">Review merges</Button>
          </Link>
        }
      />

      {health.isPending ? (
        <SkeletonStack label="Loading vocabulary health">
          <Skeleton width={280} height={28} />
          <Skeleton width={620} />
          <Skeleton width={452} />
        </SkeletonStack>
      ) : health.error ? (
        <ErrorState
          headline="Couldn't load vocabulary health"
          detail="The health report did not respond. Try again in a moment."
        />
      ) : health.data === null || health.data === undefined ? (
        <EmptyState
          icon="info"
          headline="Vocabulary health isn't available yet"
          body="This corpus has not been reconciled. Publish a document and the first report is built on the next pass."
        />
      ) : (
        <Findings
          orgSlug={orgSlug}
          findings={health.data}
          pendingMerges={pending?.length ?? 0}
          lastRun={merges.data?.last_run_at ?? null}
          tab={tab}
          onTab={setTab}
          showDismissed={showDismissed}
          onShowDismissed={setShowDismissed}
          /* Guarded on the ARRAY, never on `data`: the payload is an `as` cast
           * over untyped JSON, so a 200 carrying some other body is
           * `data`-truthy, typechecks, and then dies inside `.find`. */
          ledgerEntries={
            Array.isArray(ledger.data?.entries) ? ledger.data.entries : []
          }
        />
      )}
    </>
  );
}

type FindingsProps = {
  orgSlug: string;
  findings: WorkListFinding[];
  pendingMerges: number;
  lastRun: string | null;
  tab: TabKey;
  onTab: (tab: TabKey) => void;
  showDismissed: boolean;
  onShowDismissed: (show: boolean) => void;
  /** Empty when the ledger has not resolved or returned something unexpected.
   *  Empty means no proof line anywhere, which is the honest degradation. */
  ledgerEntries: LedgerEntry[];
};

function Findings({
  orgSlug,
  findings,
  pendingMerges,
  lastRun,
  tab,
  onTab,
  showDismissed,
  onShowDismissed,
  ledgerEntries,
}: FindingsProps) {
  const assign = useAssignFinding(orgSlug);

  const byKind = (kind: TabKey) =>
    findings.filter((finding) => finding.kind === kind);

  const active = TABS.find((entry) => entry.key === tab) ?? TABS[0];
  const ofKind = byKind(active.key);
  const rows = packRows(ofKind);
  // The cell is a plain anchor, not a router Link, so the URL is built here
  // rather than through a typed route.
  const mergesHref = `/corpora/${encodeURIComponent(orgSlug)}/merges`;

  // A full replace, not a patch — the server takes all three fields together,
  // so the two the reviewer did not touch are carried over from what is
  // already stored rather than defaulted, which would silently clear them.
  const save = (finding: WorkListFinding, patch: Partial<AssignInput>) =>
    assign.mutate({
      finding_key: finding.key,
      assignee: finding.assignment?.assignee ?? null,
      status: finding.assignment?.status ?? "open",
      snoozed_until: finding.assignment?.snoozed_until ?? null,
      ...patch,
    });

  const columns: TableColumn<WorkListFinding>[] = [
    {
      key: "finding",
      header: "Finding",
      cell: (finding) => {
        // Only a genuinely resolved row gets the proof line. On `no_traffic`
        // there is nothing to prove — attaching a rewrite's verdict to a
        // window nobody visited would read as cause and effect.
        const proof =
          finding.unranked_kind === "resolved"
            ? fixAndProveFor(finding, ledgerEntries)
            : undefined;
        return (
          <>
            <span className="maisight-content-worklist-term type-numSm">
              {active.key === "inconsistent" ? (
                <a className="maisight-content-doc-link" href={mergesHref}>
                  {finding.term}
                </a>
              ) : (
                finding.term
              )}
              {/* The badge marks the state at a glance; it never REPLACES the
                * sentence below, which carries the detail a pill cannot.
                * Snoozed is a different thing again and shows in the Snooze
                * column as "Wake (date)", so the three never collide.
                *
                * ONLY `resolved` GETS THE WIN TREATMENT. A `no_traffic` row
                * has the same shape and the opposite meaning — nobody
                * visited, so nothing is proven — and a green "Resolved" pill
                * over it would manufacture a success out of missing visits.
                * An older payload with no `unranked_kind` claims neither. */}
              {finding.unranked_reason ? (
                <Badge tone={finding.unranked_kind === "resolved" ? "nominal" : "neutral"}>
                  <span title={finding.unranked_reason}>
                    {finding.unranked_kind === "resolved" ? "Resolved" : "No visits"}
                  </span>
                </Badge>
              ) : null}
            </span>
            <span className="maisight-content-worklist-detail">{finding.detail}</span>
            {/* The proof, and the path A3 -> A1 that did not exist before. No
              * matching rewrite means NO link — a dead link into an empty
              * state is worse than none. */}
            {proof ? (
              <Link
                className="maisight-content-worklist-proof"
                to={hrefs(orgSlug).analytics}
              >
                {proofLabel(proof)}
              </Link>
            ) : null}
          </>
        );
      },
    },
    {
      key: "owner",
      header: "Owner",
      cell: (finding) => (
        <OwnerCell
          finding={finding}
          onSave={(assignee) => save(finding, { assignee })}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (finding) => (
        <Select
          aria-label={`Status for ${finding.term}`}
          className="maisight-content-worklist-status"
          value={finding.assignment?.status ?? "open"}
          options={STATUS_OPTIONS}
          onChange={(event) =>
            save(finding, { status: event.target.value as AssignmentStatus })
          }
        />
      ),
    },
    {
      key: "snooze",
      header: "Snooze",
      cell: (finding) =>
        finding.assignment?.snoozed_until ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => save(finding, { snoozed_until: null })}
          >
            {`Wake (${finding.assignment.snoozed_until.slice(0, 10)})`}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => save(finding, { snoozed_until: snoozeUntil(SNOOZE_DAYS) })}
          >
            {`Snooze ${SNOOZE_DAYS}d`}
          </Button>
        ),
    },
  ];

  // Rendered above every branch below, including the healthy one: with the
  // toggle off, a corpus whose findings are all done reads as "nothing to
  // fix", and this is the only way back to them.
  const dismissedToggle = (
    <div className="maisight-content-worklist-filter">
      <Toggle
        checked={showDismissed}
        onChange={onShowDismissed}
        label="Show snoozed & done"
      />
    </div>
  );

  if (findings.length === 0) {
    return (
      <>
        {dismissedToggle}
        <EmptyState
          icon="check"
          headline="Nothing to fix"
          body="No inconsistent definitions, no unexplained jargon, and no term is holding readers up. This is the healthy state, not an empty one."
        />
      </>
    );
  }

  return (
    <>
      <StatRow>
        <StatTile
          label="Inconsistent concepts"
          value={byKind("inconsistent").length}
          detail="Defined two different ways across your docs"
        />
        <StatTile
          label="Unexplained jargon"
          value={byKind("unexplained").length}
          detail="Used once and never explained"
        />
        <StatTile
          label="Pending merge proposals"
          value={pendingMerges}
          detail={lastRun ? `Last reconciled ${lastRun.slice(0, 10)}` : "Never reconciled"}
        />
      </StatRow>

      <div className="maisight-content-tabs" role="tablist" aria-label="Finding kinds">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            className="maisight-content-tab type-label"
            aria-selected={entry.key === tab}
            onClick={() => onTab(entry.key)}
          >
            {entry.label}
            <Badge tone={entry.key === tab ? "accent" : "neutral"}>
              {byKind(entry.key).length}
            </Badge>
          </button>
        ))}
      </div>

      {dismissedToggle}

      <Card tone="flat" header={active.label}>
        <Table
          label={`${active.label} findings`}
          columns={columns}
          rows={rows}
          rowKey={(finding) => finding.key}
          /* Already packed: `packRows` applied the cap to the ACTIVE rows and
           * appended resolved ones past it, so a second slice here would undo
           * that. `total` keeps the count line honest about what was withheld. */
          maxRows={rows.length}
          total={ofKind.length}
          unit={`${active.label.toLowerCase()} findings`}
          empty={
            <EmptyState icon="check" headline="Nothing here" body={active.empty} />
          }
        />
      </Card>
    </>
  );
}

/**
 * Controlled, and re-synced when the server's value changes: an uncontrolled
 * input keeps its stale DOM value across a refetch, so a reviewer would see
 * their own discarded typing rather than what was actually saved.
 *
 * Commits on blur and on Enter, not per keystroke — one PUT per name, not one
 * per letter.
 */
function OwnerCell({
  finding,
  onSave,
}: {
  finding: WorkListFinding;
  onSave: (assignee: string | null) => void;
}) {
  const stored = finding.assignment?.assignee ?? "";
  const [value, setValue] = useState(stored);
  useEffect(() => setValue(stored), [stored]);

  const commit = () => {
    if (value.trim() === stored) return;
    onSave(value.trim() || null);
  };

  return (
    <Input
      aria-label={`Owner for ${finding.term}`}
      className="maisight-content-worklist-owner"
      placeholder="Unassigned"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}
